import React, { useState, useEffect, useRef } from 'react';
import { X, Send } from 'lucide-react';

const EMOJI_LIST = [
  '😊','😄','🙂','👍','✅','❌','🔧','📱','💬','📞',
  '🚀','⚡','🎉','💪','👋','🙏','💡','⚠️','🔴','🟢',
  '📍','🕐','💰','🛠️','✨','😅','🤝','📢','👀','💎',
];

export const DirectInterventionModal = ({ conversation, onClose, onSave }) => {
  const [message, setMessage]           = useState('');
  const [sending, setSending]           = useState(false);
  const [allMessages, setAllMessages]   = useState([]);
  const [allNotes, setAllNotes]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [actionLoading, setActionLoading]     = useState(null);
  const [reformulatedMsg, setReformulatedMsg] = useState(null);
  const [reformulating, setReformulating]     = useState(false);
  const [inputMode, setInputMode]   = useState('message');
  const [noteText, setNoteText]     = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [aiSummary, setAiSummary]   = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [showSummary, setShowSummary]       = useState(true);
  const messagesEndRef = useRef(null);
  const pollingRef     = useRef(null);
  const textareaRef    = useRef(null);

  const loadMessages = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const resp = await fetch(
        `${window.location.origin}/api/chat/conversations/${conversation.session_id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (resp.ok) {
        const data = await resp.json();
        const msgs = (data.messages || []).filter(m => m.role === 'user' || m.role === 'admin' || m.role === 'assistant');
        setAllMessages(msgs);
        setAllNotes(data.agent_notes || []);
      }
    } catch { /* silencieux */ }
    finally { setLoading(false); }
  };

  const loadAiSummary = async () => {
    setSummaryLoading(true);
    const token = localStorage.getItem('admin_token');
    try {
      const resp = await fetch(
        `${window.location.origin}/api/chat/conversations/${conversation.session_id}/summary`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (resp.ok) { const data = await resp.json(); setAiSummary(data); }
    } catch { /* silencieux */ }
    finally { setSummaryLoading(false); }
  };

  useEffect(() => {
    loadMessages();
    pollingRef.current = setInterval(loadMessages, 3000);
    loadAiSummary();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [conversation.session_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages]);

  const handleSend = async () => {
    if (!message.trim()) return;
    setReformulating(true);
    const token = localStorage.getItem('admin_token');
    try {
      const resp = await fetch(`${window.location.origin}/api/chat/reformulate-message`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: message.trim(), session_id: conversation.session_id })
      });
      if (resp.ok) { const data = await resp.json(); setReformulatedMsg(data.reformulated); }
      else { alert('❌ Erreur reformulation IA — vérifiez votre connexion'); }
    } catch { alert('❌ Erreur de communication'); }
    finally { setReformulating(false); }
  };

  const handleConfirmSend = async () => {
    if (!reformulatedMsg) return;
    setSending(true);
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${window.location.origin}/api/chat/admin-message`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: conversation.session_id, message: reformulatedMsg })
      });
      if (response.ok) { setMessage(''); setReformulatedMsg(null); await loadMessages(); onSave(); }
      else { alert('❌ Erreur lors de l\'envoi'); }
    } catch { alert('❌ Erreur de communication'); }
    finally { setSending(false); }
  };

  const handleGenerateAI = async () => {
    setGeneratingAI(true);
    const token = localStorage.getItem('admin_token');
    try {
      const resp = await fetch(`${window.location.origin}/api/chat/generate-admin-reply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: conversation.session_id })
      });
      if (resp.ok) { const data = await resp.json(); setMessage(data.suggestion || ''); textareaRef.current?.focus(); }
      else { alert('❌ Erreur génération IA'); }
    } catch { alert('❌ Erreur de communication'); }
    finally { setGeneratingAI(false); }
  };

  const insertEmoji = (emoji) => {
    const ta = textareaRef.current;
    if (!ta) { setMessage(prev => prev + emoji); setShowEmojiPicker(false); return; }
    const start = ta.selectionStart;
    const end   = ta.selectionEnd;
    setMessage(message.slice(0, start) + emoji + message.slice(end));
    setShowEmojiPicker(false);
    setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + emoji.length; ta.focus(); }, 0);
  };

  const handleEndConversation = async () => {
    if (!window.confirm('Terminer la discussion et notifier le client ?')) return;
    setActionLoading('end');
    const token = localStorage.getItem('admin_token');
    try {
      const resp = await fetch(
        `${window.location.origin}/api/chat/conversations/${conversation.session_id}/end-with-notification`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (resp.ok) { onSave(); onClose(); }
      else { alert('❌ Erreur lors de la clôture'); }
    } catch { alert('❌ Erreur de communication'); }
    finally { setActionLoading(null); }
  };

  const handleReleaseToBot = async () => {
    if (!window.confirm('Rendre la main au bot ? Le bot reprendra la conversation.')) return;
    setActionLoading('release');
    const token = localStorage.getItem('admin_token');
    try {
      const resp = await fetch(
        `${window.location.origin}/api/chat/conversations/${conversation.session_id}/release-to-bot`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (resp.ok) { onSave(); onClose(); }
      else { alert('❌ Erreur'); }
    } catch { alert('❌ Erreur de communication'); }
    finally { setActionLoading(null); }
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    const token = localStorage.getItem('admin_token');
    try {
      const resp = await fetch(
        `${window.location.origin}/api/chat/conversations/${conversation.session_id}/note`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: noteText.trim() })
        }
      );
      if (resp.ok) { setNoteText(''); await loadMessages(); }
      else { alert('❌ Erreur sauvegarde note'); }
    } catch { alert('❌ Erreur de communication'); }
    finally { setSavingNote(false); }
  };

  const userData   = conversation.user_data || {};
  const clientName = userData.name || userData.nom || 'Client';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl flex flex-col" style={{height: '85vh'}}>

        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold">💬 Intervention Directe</h3>
            <p className="text-sm text-gray-500">Client : <span className="font-semibold text-blue-600">{clientName}</span> — Le bot est muselé</p>
          </div>
          <button onClick={onClose} className="hover:bg-gray-100 p-2 rounded-full"><X size={24} /></button>
        </div>

        {/* Action bar */}
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b flex-shrink-0">
          <button onClick={handleReleaseToBot} disabled={actionLoading !== null}
            className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors"
            data-testid="release-to-bot-btn">
            <span>🤖</span>
            <span>{actionLoading === 'release' ? 'En cours...' : 'Bot reprend le relais'}</span>
          </button>
          <button onClick={handleEndConversation} disabled={actionLoading !== null}
            className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors"
            data-testid="end-conversation-btn">
            <span>✅</span>
            <span>{actionLoading === 'end' ? 'Clôture...' : 'Terminer — Client notifié'}</span>
          </button>
        </div>

        {/* AI Summary */}
        <div className={`transition-all duration-300 overflow-hidden ${showSummary ? 'max-h-40' : 'max-h-0'}`}>
          <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-100 flex-shrink-0">
            {summaryLoading ? (
              <div className="flex items-center gap-2 text-indigo-400 text-xs">
                <span className="animate-spin">⏳</span>
                <span>Analyse IA de la conversation...</span>
              </div>
            ) : aiSummary ? (
              <div className="flex items-start gap-2">
                <span className="text-indigo-500 text-sm mt-0.5 flex-shrink-0">🧠</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-indigo-700 mb-0.5">Résumé IA — {aiSummary.client} · {aiSummary.message_count} messages</p>
                  <p className="text-xs text-indigo-800 leading-relaxed whitespace-pre-line">{aiSummary.summary}</p>
                </div>
                <button onClick={() => setShowSummary(false)} className="text-indigo-300 hover:text-indigo-500 text-xs flex-shrink-0 mt-0.5">✕</button>
              </div>
            ) : null}
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {loading && <div className="text-center text-gray-400 py-8">Chargement de l'historique...</div>}
          {!loading && allMessages.length === 0 && (
            <div className="text-center text-gray-400 py-8">Aucun message — démarrez la conversation</div>
          )}
          {(() => {
            const timeline = [
              ...allMessages.map(m => ({ ...m, _type: 'message' })),
              ...allNotes.map(n => ({ ...n, _type: 'note' }))
            ].sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));

            return timeline.map((item, idx) => {
              if (item._type === 'note') {
                return (
                  <div key={`note-${idx}`} className="mx-2">
                    <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-2.5">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">📝 Note interne</span>
                        <span className="text-xs text-amber-600">— {item.author}</span>
                      </div>
                      <p className="text-sm text-amber-900 whitespace-pre-wrap">{item.content}</p>
                      {item.timestamp && (
                        <p className="text-xs text-amber-500 mt-1">
                          {new Date(item.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                  </div>
                );
              }
              return (
                <div key={`msg-${idx}`} className={`flex ${item.role === 'admin' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl text-sm ${
                    item.role === 'admin'
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : item.role === 'assistant'
                        ? 'bg-purple-100 border border-purple-200 text-purple-900 rounded-bl-sm shadow-sm'
                        : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm'
                  }`}>
                    <p className={`text-xs font-semibold mb-1 ${
                      item.role === 'admin' ? 'text-blue-200'
                        : item.role === 'assistant' ? 'text-purple-500'
                        : 'text-gray-500'
                    }`}>
                      {item.role === 'admin' ? '👤 Agent' : item.role === 'assistant' ? '🤖 Bot Sophie' : `💬 ${clientName}`}
                    </p>
                    <p className="whitespace-pre-wrap">{item.content}</p>
                    {item.timestamp && (
                      <p className={`text-xs mt-1 ${item.role === 'admin' ? 'text-blue-200' : 'text-gray-400'}`}>
                        {new Date(item.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>
              );
            });
          })()}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="p-4 border-t flex-shrink-0 bg-white">
          {reformulatedMsg === null && (
            <div className="flex gap-1 mb-3 bg-gray-100 rounded-xl p-1 w-fit">
              <button onClick={() => setInputMode('message')}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${inputMode === 'message' ? 'bg-white text-blue-700 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="mode-message-tab">
                💬 Message client
              </button>
              <button onClick={() => setInputMode('note')}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${inputMode === 'note' ? 'bg-amber-100 text-amber-700 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="mode-note-tab">
                📝 Note interne
              </button>
            </div>
          )}

          {/* Note mode */}
          {inputMode === 'note' && reformulatedMsg === null && (
            <div className="space-y-2">
              <p className="text-xs text-amber-600 font-semibold">🔒 Note visible uniquement par les agents — jamais envoyée au client</p>
              <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && noteText.trim()) { e.preventDefault(); handleSaveNote(); } }}
                className="w-full border-2 border-amber-300 rounded-xl px-4 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-amber-400 bg-amber-50 text-amber-900 placeholder-amber-400"
                rows={3} placeholder="Mémo interne, contexte client, suivi dossier..." data-testid="note-input" />
              <button onClick={handleSaveNote} disabled={savingNote || !noteText.trim()}
                className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white py-2 rounded-xl font-semibold text-sm transition-colors"
                data-testid="save-note-btn">
                {savingNote ? '⏳ Sauvegarde...' : '📝 Enregistrer la note'}
              </button>
            </div>
          )}

          {/* Reformulated message review */}
          {inputMode === 'message' && reformulatedMsg !== null ? (
            <div className="space-y-3">
              <p className="text-xs font-bold text-purple-700 uppercase tracking-wide">✨ Version reformulée par l'IA — vérifiez avant d'envoyer</p>
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                <p className="text-xs text-gray-400 font-semibold mb-0.5">Votre brouillon :</p>
                <p className="text-sm text-gray-400 line-through whitespace-pre-wrap">{message}</p>
              </div>
              <div className="bg-purple-50 border-2 border-purple-300 rounded-xl px-3 py-2">
                <p className="text-xs text-purple-600 font-semibold mb-1">Version IA (modifiable) :</p>
                <textarea value={reformulatedMsg} onChange={e => setReformulatedMsg(e.target.value)}
                  className="w-full bg-transparent text-sm text-gray-900 resize-none focus:outline-none whitespace-pre-wrap" rows={3} />
              </div>
              <div className="flex gap-2">
                <button onClick={handleConfirmSend} disabled={sending || !reformulatedMsg.trim()}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white py-2 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
                  data-testid="confirm-send-btn">
                  <Send size={15} />
                  <span>{sending ? 'Envoi...' : '✅ Envoyer au client'}</span>
                </button>
                <button onClick={() => setReformulatedMsg(null)}
                  className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 py-2 rounded-xl font-semibold text-sm transition-colors"
                  data-testid="edit-again-btn">
                  ✏️ Modifier à nouveau
                </button>
              </div>
            </div>
          ) : inputMode === 'message' ? (
            <>
              {/* Toolbar */}
              <div className="flex items-center space-x-2 mb-2">
                <button onClick={handleGenerateAI} disabled={generatingAI}
                  className="flex items-center space-x-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors"
                  data-testid="ai-suggest-btn">
                  {generatingAI ? <span className="animate-spin mr-1">⏳</span> : <span className="mr-1">✨</span>}
                  <span>{generatingAI ? 'Génération...' : 'Suggérer IA'}</span>
                </button>
                <div className="relative">
                  <button onClick={() => setShowEmojiPicker(p => !p)}
                    className="flex items-center space-x-1 bg-yellow-400 hover:bg-yellow-500 text-white text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors"
                    data-testid="emoji-picker-btn">
                    <span>😊</span><span>Emoji</span>
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute bottom-9 left-0 bg-white border border-gray-200 rounded-xl shadow-xl p-3 z-50 w-64">
                      <div className="grid grid-cols-10 gap-1">
                        {EMOJI_LIST.map(e => (
                          <button key={e} onClick={() => insertEmoji(e)}
                            className="text-lg hover:bg-gray-100 rounded p-0.5 transition-colors">{e}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Textarea + send */}
              <div className="flex space-x-3">
                <textarea ref={textareaRef} value={message} onChange={e => setMessage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && message.trim()) { e.preventDefault(); handleSend(); } }}
                  className="flex-1 border rounded-xl px-4 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                  rows={2} placeholder="Rédigez votre brouillon... (l'IA le corrigera avant envoi)" data-testid="admin-message-input" />
                <button onClick={handleSend} disabled={reformulating || !message.trim()}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-xl font-semibold flex items-center gap-1.5 flex-shrink-0 text-sm"
                  data-testid="admin-send-btn">
                  {reformulating ? <span className="animate-spin">⏳</span> : <Send size={16} />}
                  <span>{reformulating ? 'IA...' : 'Vérifier'}</span>
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">L'IA corrige votre message avant que vous l'envoyiez</p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
